// Pure state machine for the drill loop: LISTEN -> SPEAK -> FEEDBACK -> next.
// No timers, DOM or async: the component owns timers and runs the returned
// effects. transition() never mutates its input.
// SPEAK sub-states (live mic, `scoring`, `asrError`/`showEmptyHint`) are flags,
// not statuses, so consumers never have to learn a new status.

export const PASS_THRESHOLD = 80

// Quiet after an utterance before submitting. Local VAD is instant, so this
// only needs to cover natural mid-sentence pauses.
export const SILENCE_MS = 1800
// Hard cap on one SPEAK attempt: LEAD + natural duration x MULTIPLIER, clamped
// to [FLOOR, CEILING] (BUGFIX-004 §4, STORY-003). The cap is visible and submits
// the take, so too tight truncates real speech mid-word. LEAD covers the ~165ms
// cue gate plus reaction time; MULT covers learners running slower than source.
// ponytail: calibration knobs from censored data (every take hit the cap).
// Widen if takes still truncate; watch `used` vs `cap` in drillController logs.
export const MAX_ATTEMPT_FLOOR_MS = 3000
export const MAX_ATTEMPT_LEAD_MS = 1200
export const MAX_ATTEMPT_MULTIPLIER = 1.8
// Bounds a bad caption merge; ~30s of Opus stays under asr.js MAX_CLIP_BYTES.
export const MAX_ATTEMPT_CEILING_MS = 30000

export function attemptCapMs (sentence) {
  var naturalMs = sentence ? Math.max(0, (sentence.end - sentence.start) * 1000) : 0
  var scaled = MAX_ATTEMPT_LEAD_MS + naturalMs * MAX_ATTEMPT_MULTIPLIER
  return Math.min(MAX_ATTEMPT_CEILING_MS, Math.max(MAX_ATTEMPT_FLOOR_MS, scaled))
}
export const EMPTY_HINT_MS = 8000
// Consecutive empty attempts before the drill stops re-arming the cap timer.
// A dead mic would otherwise re-cue forever, and repeated audio-session churn
// is what breaks iOS (BUGFIX-002/003/004). Past this the mic stays open, untimed.
export const MAX_EMPTY_ATTEMPTS = 2
export const ADVANCE_DELAY_MS = 2000
export const ADVANCE_MIN_MS = 4000
export const REPLAY_HOLD_MS = 1000

// STORY-002: below this intro/outro gap, cruising is not worth the seek.
export const MIN_CRUISE_S = 1.5
// Subtracted from the target start so the 250ms cruise poll fires before the
// sentence's first syllable.
export const LEAD_IN_S = 0.3

export const EFFECTS = {
  PLAY_SENTENCE: 'PLAY_SENTENCE',
  PLAY_SEGMENT: 'PLAY_SEGMENT',
  CRUISE_TO: 'CRUISE_TO',
  SET_START_TIME: 'SET_START_TIME',
  PAUSE_VIDEO: 'PAUSE_VIDEO',
  PLAY_SPEAK_CUE: 'PLAY_SPEAK_CUE',
  CLEAR_TEXT: 'CLEAR_TEXT',
  START_SPEAK_TIMERS: 'START_SPEAK_TIMERS',
  STOP_SPEAK_TIMERS: 'STOP_SPEAK_TIMERS',
  RESET_ATTEMPT: 'RESET_ATTEMPT',
  START_ADVANCE_TIMER: 'START_ADVANCE_TIMER',
  STOP_ADVANCE_TIMER: 'STOP_ADVANCE_TIMER',
  START_REPLAY_HOLD_TIMER: 'START_REPLAY_HOLD_TIMER',
  STOP_REPLAY_HOLD_TIMER: 'STOP_REPLAY_HOLD_TIMER',
  STOP_ALL_TIMERS: 'STOP_ALL_TIMERS',
  RECORD_HISTORY: 'RECORD_HISTORY',
  LOAD_TRANSCRIPT: 'LOAD_TRANSCRIPT',
  TRACK_READY: 'TRACK_READY',
  TRACK_DRILL_STARTED: 'TRACK_DRILL_STARTED',
  TRACK_SCORED: 'TRACK_SCORED',
  TRACK_SEGMENT_COMPLETED: 'TRACK_SEGMENT_COMPLETED',
  TRACK_UNSUPPORTED: 'TRACK_UNSUPPORTED',
  TRACK_MIC_DENIED: 'TRACK_MIC_DENIED',
  TRACK_FINISHED: 'TRACK_FINISHED'
}

/**
 * @returns {Object} a fresh drill state (status 'loading')
 */
export function createDrillState () {
  return {
    status: 'loading',
    errorCode: null,
    sentenceIndex: 0,
    sentenceCount: 0,
    attemptNumber: 1,
    lastAccuracy: null,
    scores: [],
    practicedIndices: [],
    showEmptyHint: false,
    autoAdvancing: false,
    autoRetrying: false,
    replaying: false,
    replayPending: false,
    scoring: false,
    asrError: null,
    // see MAX_EMPTY_ATTEMPTS
    emptyAttempts: 0,
    micStalled: false,
    // STORY-002: listen+cruising waits for the sentence START instead of END,
    // so intro/outro plays through. A flag, not a status.
    cruising: false
  }
}

// Per-attempt flags, cleared together whenever an attempt starts or is
// abandoned so no stale spinner/error/hint survives. The empty-attempt count
// is per sentence turn, so any deliberate move restarts it.
const ATTEMPT_RESET = {
  scoring: false,
  asrError: null,
  showEmptyHint: false,
  emptyAttempts: 0,
  micStalled: false
}

function noop (state) {
  return { state: state, effects: [] }
}

function clampIndex (index, count) {
  return Math.max(0, Math.min(index, Math.max(count - 1, 0)))
}

export const ACTIVE_STATUSES = ['listen', 'speak', 'feedback']

/**
 * Compute the next drill state for an event. Pure: never mutates `state`.
 * Events invalid for the current status are ignored, returning the SAME
 * state object (identity) with an empty effects list, so the component can
 * cheaply discard stale timer callbacks.
 *
 * @param {Object} state current drill state
 * @param {{type: string}} event
 * @returns {{state: Object, effects: string[]}}
 */
export function transition (state, event) {
  switch (event.type) {
    case 'SENTENCES_READY': {
      if (state.status !== 'loading') return noop(state)
      return {
        state: {
          ...state,
          status: 'ready',
          sentenceCount: event.count,
          sentenceIndex: event.index,
          attemptNumber: 1
        },
        effects: [EFFECTS.TRACK_READY]
      }
    }

    case 'TRANSCRIPT_FAILED': {
      if (state.status !== 'loading') return noop(state)
      return {
        state: { ...state, status: 'unsupported', errorCode: event.code },
        effects: [EFFECTS.TRACK_UNSUPPORTED]
      }
    }

    case 'RETRY_LOAD': {
      // Also valid from 'loading' (stalled-fetch escape hatch). Must return a
      // new state object, not noop, or the component skips the effect.
      if (state.status !== 'unsupported' && state.status !== 'loading') return noop(state)
      return {
        state: { ...state, status: 'loading', errorCode: null },
        effects: [EFFECTS.LOAD_TRANSCRIPT]
      }
    }

    case 'PLAY_TAPPED': {
      if (state.status !== 'ready') return noop(state)
      // STORY-002 intro cruise: sentence 0 only. introGapS is 0 when a
      // share/challenge link is active, so that case never clears MIN_CRUISE_S.
      const cruising = state.sentenceIndex === 0 && (event.introGapS || 0) >= MIN_CRUISE_S
      return {
        state: { ...state, status: 'listen', replaying: false, cruising: cruising },
        effects: [cruising ? EFFECTS.CRUISE_TO : EFFECTS.PLAY_SENTENCE, EFFECTS.TRACK_DRILL_STARTED]
      }
    }

    case 'SENTENCE_ENDED': {
      if (state.status !== 'listen') return noop(state)
      return {
        state: { ...state, ...ATTEMPT_RESET, status: 'speak', replaying: false },
        // Cue after the video pauses and before the mic opens, so it neither
        // overlaps the sentence tail nor lands in the recording.
        effects: [EFFECTS.PAUSE_VIDEO, EFFECTS.PLAY_SPEAK_CUE, EFFECTS.CLEAR_TEXT, EFFECTS.START_SPEAK_TIMERS]
      }
    }

    // Upload in flight; status stays 'speak', only the flag changes.
    case 'ATTEMPT_SUBMITTED': {
      if (state.status !== 'speak' || state.scoring) return noop(state)
      // Deliberately keeps emptyAttempts: an upload proves nothing. Only a
      // score (or SPEECH_DETECTED) resets it, or an ASR returning empty text
      // would loop submit -> abort forever without ever stalling.
      return {
        state: { ...state, scoring: true, asrError: null, showEmptyHint: false },
        effects: [EFFECTS.STOP_SPEAK_TIMERS]
      }
    }

    // Nothing scoreable (silence or failed ASR): hand the mic back, with the
    // cue so the refilled countdown reads as a new attempt.
    case 'ATTEMPT_ABORTED': {
      if (state.status !== 'speak') return noop(state)
      const empty = !event.code
      // Only codeless aborts count toward a stall: a network/timeout error
      // says nothing about the mic.
      const emptyAttempts = empty ? state.emptyAttempts + 1 : state.emptyAttempts
      const micStalled = emptyAttempts >= MAX_EMPTY_ATTEMPTS
      return {
        state: {
          ...state,
          scoring: false,
          asrError: event.code || null,
          showEmptyHint: empty,
          emptyAttempts: emptyAttempts,
          micStalled: micStalled
        },
        effects: micStalled
          ? [EFFECTS.START_SPEAK_TIMERS]
          : [EFFECTS.PLAY_SPEAK_CUE, EFFECTS.START_SPEAK_TIMERS]
      }
    }

    // Retract the stale "didn't catch that" hint once the user talks again.
    case 'SPEECH_DETECTED': {
      if (state.status !== 'speak' || state.scoring) return noop(state)
      if (!state.showEmptyHint && !state.asrError && !state.micStalled) return noop(state)
      // Clearing the stall does not re-time this attempt (the controller reads
      // micStalled only in startSpeakTimers); it restores the cap next attempt.
      return {
        state: { ...state, showEmptyHint: false, asrError: null, emptyAttempts: 0, micStalled: false },
        effects: []
      }
    }

    // User discard: restart capture on the same sentence, no score recorded.
    case 'RESET_TAPPED': {
      if (state.status !== 'speak' || state.scoring) return noop(state)
      // Cue so the refilled ring reads as a fresh start. gateMic() parks the
      // cue promise on the controller, so it still gates the async reopen.
      return {
        state: { ...state, ...ATTEMPT_RESET },
        effects: [EFFECTS.PLAY_SPEAK_CUE, EFFECTS.CLEAR_TEXT, EFFECTS.RESET_ATTEMPT]
      }
    }

    case 'ATTEMPT_SCORED': {
      if (state.status !== 'speak') return noop(state)
      const accuracy = event.accuracy
      const pass = accuracy >= PASS_THRESHOLD
      const scores = state.scores.concat([accuracy])
      const practicedIndices = state.practicedIndices.indexOf(state.sentenceIndex) === -1
        ? state.practicedIndices.concat([state.sentenceIndex])
        : state.practicedIndices
      const effects = [EFFECTS.STOP_SPEAK_TIMERS, EFFECTS.RECORD_HISTORY, EFFECTS.TRACK_SCORED]
      effects.push(pass ? EFFECTS.START_ADVANCE_TIMER : EFFECTS.START_REPLAY_HOLD_TIMER)
      return {
        state: {
          ...state,
          status: 'feedback',
          lastAccuracy: accuracy,
          scores: scores,
          practicedIndices: practicedIndices,
          ...ATTEMPT_RESET,
          autoAdvancing: pass,
          autoRetrying: !pass,
          replaying: false,
          replayPending: !pass
        },
        effects: effects
      }
    }

    case 'REPLAY_HOLD_ELAPSED': {
      if (state.status !== 'feedback' || !state.replayPending) return noop(state)
      return {
        state: { ...state, replayPending: false, replaying: true },
        effects: [EFFECTS.PLAY_SEGMENT]
      }
    }

    case 'REPLAY_ENDED': {
      if (state.status !== 'feedback' || !state.replaying) return noop(state)
      const effects = [EFFECTS.PAUSE_VIDEO]
      if (state.autoAdvancing || state.autoRetrying) effects.push(EFFECTS.START_ADVANCE_TIMER)
      return {
        state: { ...state, replaying: false },
        effects: effects
      }
    }

    case 'HEAR_AGAIN': {
      if (state.status !== 'feedback' || state.replaying) return noop(state)
      return {
        state: { ...state, replaying: true, replayPending: false, autoAdvancing: false, autoRetrying: false },
        effects: [EFFECTS.STOP_ADVANCE_TIMER, EFFECTS.STOP_REPLAY_HOLD_TIMER, EFFECTS.PLAY_SEGMENT]
      }
    }

    case 'ADVANCE_TIMER_ELAPSED': {
      if (state.status !== 'feedback' || (!state.autoAdvancing && !state.autoRetrying)) return noop(state)

      if (state.autoRetrying) {
        return {
          state: {
            ...state,
            ...ATTEMPT_RESET,
            status: 'speak',
            attemptNumber: state.attemptNumber + 1,
            autoRetrying: false,
            replaying: false,
            replayPending: false
          },
          effects: [EFFECTS.PAUSE_VIDEO, EFFECTS.PLAY_SPEAK_CUE, EFFECTS.CLEAR_TEXT, EFFECTS.START_SPEAK_TIMERS]
        }
      }

      const isLast = state.sentenceIndex >= state.sentenceCount - 1

      if (isLast) {
        // STORY-002 outro cruise: play the clip's tail before 'done'. There is
        // no duration signal to size the gap, so always try; CRUISE_ARRIVED
        // lands on 'done' once the poll sees playback genuinely stopped.
        return {
          state: { ...state, status: 'listen', cruising: true, autoAdvancing: false, replaying: false },
          effects: [EFFECTS.CRUISE_TO, EFFECTS.TRACK_SEGMENT_COMPLETED]
        }
      }

      return {
        state: {
          ...state,
          status: 'listen',
          sentenceIndex: state.sentenceIndex + 1,
          attemptNumber: 1,
          autoAdvancing: false,
          replaying: false,
          cruising: false
        },
        effects: [EFFECTS.PLAY_SENTENCE, EFFECTS.TRACK_SEGMENT_COMPLETED]
      }
    }

    // STORY-002: an already-scored sentence means outro arrival (done);
    // otherwise it is an intro arrival.
    case 'CRUISE_ARRIVED': {
      if (state.status !== 'listen' || !state.cruising) return noop(state)
      if (state.practicedIndices.indexOf(state.sentenceIndex) !== -1) {
        return {
          state: { ...state, status: 'done', cruising: false },
          effects: [EFFECTS.TRACK_FINISHED]
        }
      }
      return { state: { ...state, cruising: false }, effects: [] }
    }

    case 'CANCEL_ADVANCE': {
      if (state.status !== 'feedback' || (!state.autoAdvancing && !state.autoRetrying)) return noop(state)
      return {
        state: { ...state, autoAdvancing: false, autoRetrying: false },
        effects: [EFFECTS.STOP_ADVANCE_TIMER]
      }
    }

    case 'RETRY_TAPPED': {
      if (state.status !== 'feedback') return noop(state)
      return {
        state: {
          ...state,
          ...ATTEMPT_RESET,
          status: 'speak',
          attemptNumber: state.attemptNumber + 1,
          autoAdvancing: false,
          autoRetrying: false,
          replaying: false,
          replayPending: false
        },
        effects: [EFFECTS.PAUSE_VIDEO, EFFECTS.PLAY_SPEAK_CUE, EFFECTS.STOP_ADVANCE_TIMER, EFFECTS.STOP_REPLAY_HOLD_TIMER, EFFECTS.CLEAR_TEXT, EFFECTS.START_SPEAK_TIMERS]
      }
    }

    case 'NEXT_TAPPED': {
      if (state.status === 'ready') {
        const nextIndex = clampIndex(state.sentenceIndex + 1, state.sentenceCount)
        if (nextIndex === state.sentenceIndex) {
          return { state: { ...state, attemptNumber: 1 }, effects: [] }
        }
        return {
          state: { ...state, sentenceIndex: nextIndex, attemptNumber: 1 },
          effects: [EFFECTS.SET_START_TIME]
        }
      }

      if (ACTIVE_STATUSES.indexOf(state.status) !== -1) {
        const isLast = state.sentenceIndex >= state.sentenceCount - 1
        const fromFeedback = state.status === 'feedback'

        if (fromFeedback && isLast) {
          return {
            state: { ...state, ...ATTEMPT_RESET, status: 'done', autoAdvancing: false, autoRetrying: false, replaying: false },
            effects: [EFFECTS.STOP_ALL_TIMERS, EFFECTS.TRACK_SEGMENT_COMPLETED, EFFECTS.TRACK_FINISHED]
          }
        }

        if (isLast) {
          return noop(state)
        }

        const effects = [EFFECTS.STOP_ALL_TIMERS, EFFECTS.PLAY_SENTENCE]
        if (fromFeedback) effects.push(EFFECTS.TRACK_SEGMENT_COMPLETED)
        return {
          state: {
            ...state,
            status: 'listen',
            sentenceIndex: state.sentenceIndex + 1,
            attemptNumber: 1,
            ...ATTEMPT_RESET,
            autoAdvancing: false,
            autoRetrying: false,
            replaying: false,
            // STORY-002 §3: navigation never cruises; without this the spread
            // would carry cruising into the next sentence.
            cruising: false
          },
          effects: effects
        }
      }

      return noop(state)
    }

    case 'PREV_TAPPED': {
      if (state.status === 'ready') {
        const prevIndex = clampIndex(state.sentenceIndex - 1, state.sentenceCount)
        if (prevIndex === state.sentenceIndex) {
          return { state: { ...state, attemptNumber: 1 }, effects: [] }
        }
        return {
          state: { ...state, sentenceIndex: prevIndex, attemptNumber: 1 },
          effects: [EFFECTS.SET_START_TIME]
        }
      }

      if (ACTIVE_STATUSES.indexOf(state.status) !== -1) {
        const prevIndex = clampIndex(state.sentenceIndex - 1, state.sentenceCount)
        return {
          state: {
            ...state,
            status: 'listen',
            sentenceIndex: prevIndex,
            attemptNumber: 1,
            ...ATTEMPT_RESET,
            autoAdvancing: false,
            autoRetrying: false,
            replaying: false,
            cruising: false // STORY-002 §3: PREV always seeks, never cruises
          },
          effects: [EFFECTS.STOP_ALL_TIMERS, EFFECTS.PLAY_SENTENCE]
        }
      }

      return noop(state)
    }

    case 'REPLAY_TAPPED': {
      if (ACTIVE_STATUSES.indexOf(state.status) === -1) return noop(state)
      return {
        state: {
          ...state,
          status: 'listen',
          ...ATTEMPT_RESET,
          autoAdvancing: false,
          autoRetrying: false,
          replaying: false,
          cruising: false // STORY-002 §3: replay restarts the sentence, never cruises
        },
        effects: [EFFECTS.STOP_ALL_TIMERS, EFFECTS.PLAY_SENTENCE]
      }
    }

    case 'EMPTY_TIMEOUT': {
      if (state.status !== 'speak') return noop(state)
      return {
        state: { ...state, showEmptyHint: true },
        effects: []
      }
    }

    case 'MIC_DENIED': {
      return {
        state: {
          ...state,
          status: 'micBlocked',
          ...ATTEMPT_RESET,
          autoAdvancing: false,
          autoRetrying: false,
          replaying: false,
          cruising: false
        },
        effects: [EFFECTS.PAUSE_VIDEO, EFFECTS.STOP_ALL_TIMERS, EFFECTS.TRACK_MIC_DENIED]
      }
    }

    case 'MIC_GRANTED': {
      if (state.status !== 'micBlocked') return noop(state)
      // Recovery always seeks back, never re-cruises an intro.
      return {
        state: { ...state, ...ATTEMPT_RESET, status: 'listen', replaying: false, cruising: false },
        effects: [EFFECTS.PLAY_SENTENCE]
      }
    }

    case 'PRACTICE_AGAIN': {
      if (state.status !== 'done') return noop(state)
      const cruising = (event.introGapS || 0) >= MIN_CRUISE_S
      return {
        state: {
          ...state,
          status: 'listen',
          sentenceIndex: 0,
          attemptNumber: 1,
          scores: [],
          practicedIndices: [],
          lastAccuracy: null,
          ...ATTEMPT_RESET,
          autoAdvancing: false,
          autoRetrying: false,
          replaying: false,
          cruising: cruising
        },
        effects: [cruising ? EFFECTS.CRUISE_TO : EFFECTS.PLAY_SENTENCE, EFFECTS.TRACK_DRILL_STARTED]
      }
    }

    case 'VIDEO_CHANGED': {
      return { state: createDrillState(), effects: [EFFECTS.STOP_ALL_TIMERS, EFFECTS.LOAD_TRANSCRIPT] }
    }

    // Settings changed the start time: relocate from ANY status, discarding
    // whatever attempt was in progress.
    case 'STARTTIME_CHANGED': {
      if (state.sentenceCount === 0) return noop(state)
      return {
        state: {
          ...state,
          status: 'ready',
          sentenceIndex: clampIndex(event.index, state.sentenceCount),
          attemptNumber: 1,
          autoAdvancing: false,
          autoRetrying: false,
          ...ATTEMPT_RESET,
          replaying: false,
          replayPending: false,
          // STORY-002 §3: explicit reposition never cruises.
          cruising: false
        },
        effects: [EFFECTS.STOP_ALL_TIMERS]
      }
    }

    case 'SUSPEND': {
      if (ACTIVE_STATUSES.indexOf(state.status) === -1) return noop(state)
      return {
        state: {
          ...state,
          status: 'ready',
          attemptNumber: 1,
          ...ATTEMPT_RESET,
          autoAdvancing: false,
          autoRetrying: false,
          replaying: false,
          cruising: false
        },
        effects: [EFFECTS.PAUSE_VIDEO, EFFECTS.STOP_ALL_TIMERS]
      }
    }

    default:
      return noop(state)
  }
}

/**
 * Session-level stats for the DONE card.
 */
export function sessionSummary (state) {
  const practicedCount = state.practicedIndices.length
  const avgAccuracy = state.scores.length === 0
    ? null
    : Math.round(state.scores.reduce((sum, score) => sum + score, 0) / state.scores.length)
  // A single pass separates "hard" from "broken" even when the average is low.
  const everPassed = state.scores.some(score => score >= PASS_THRESHOLD)
  return { practicedCount: practicedCount, avgAccuracy: avgAccuracy, everPassed: everPassed }
}
